export type Persona = {
  slug: string;
  title: string; // Short page title, e.g. "Busy Mums"
  h1: string; // Full H1 context line
  description: string; // Meta description-friendly copy
  tone: 'reassuring' | 'respectful' | 'energetic' | 'bold' | 'warm';
  howHelps: string[]; // Bullet points on how Explanatum helps
  // Optional default skills to prefill the wizard (comma IDs from Idea wizard taxonomy)
  defaultSkillIds?: string[];
  // Spintax-capable intro variants for uniqueness
  introVariants?: string[];
  // Persona-specific FAQs
  faqs?: { q: string; a: string }[];
  // Example ideas tailored to persona
  exampleIdeas?: {
    title: string;
    whyFit: string;
    steps: string[];
    tools?: string[];
    costRange?: string; // e.g., "£1–£50"
  }[];
  // Mini playbook varying by time commitment
  playbookByTime?: Record<string, string[]>; // key = timeSlug
};

export const PERSONAS: Record<string, Persona> = {
  'recently-redundant': {
    slug: 'recently-redundant',
    title: 'Recently Redundant',
    h1: 'Business ideas for professionals recently made redundant',
    description:
      "You've got more leverage than you think. Reframe your experience into consulting or lean online ventures with low upfront risk.",
    tone: 'reassuring',
    howHelps: [
      'Reframe experience into consulting, logistics, or eCommerce niches',
      'Test ideas without upfront cost using guided modules',
      'Use cold outreach tools to pitch yourself—skip gatekeepers',
    ],
    defaultSkillIds: ['operations', 'logistics', 'ecommerce', 'consulting'],
    introVariants: [
      '{Turn your redundancy into leverage|Redundancy can be a launchpad}: pair your experience with {lean|low-risk} business models and validate fast.',
      '{Consult|Contract} first, productize later—our checklists de-risk each step {without big spend|on a tiny budget}.',
    ],
    faqs: [
      { q: 'Do I need money to start?', a: 'You can start from as little as £1 using our no-cost validation playbooks and outreach templates.' },
      { q: 'What if I want to invest more?', a: 'We support paths from £1 up to seven figures with staged investment planning and risk analysis.' },
    ],
    exampleIdeas: [
      {
        title: 'Fractional Ops Consultant for Local SMEs',
        whyFit: 'Maps corporate ops experience to local businesses needing process fixes.',
        steps: ['Define 3 packaged offers', 'Email 50 prospects with a 2-step sequence', 'Deliver a 2-week pilot'],
        tools: ['Gmail outreach', 'Notion SOPs', 'Stripe'],
        costRange: '£1–£100',
      },
      {
        title: 'Niche Shopify Setup + Handover',
        whyFit: 'Low CAPEX service using templates and checklists.',
        steps: ['Pick 2 niches', 'Create demo store', 'Pitch 20 local merchants'],
        tools: ['Shopify trial', 'Canva', ' Loom'],
        costRange: '£25–£200',
      },
    ],
    playbookByTime: {
      'weekend': ['Saturday: research and shortlist niches', 'Sunday: send first 20 outreach emails'],
      'part-time': ['Mon: prospecting', 'Wed: calls', 'Sat: deliverables'],
      'full-time': ['Daily pipeline targets', 'Weekly delivery cadence', 'Bi-weekly retros'],
    },
  },
  'long-term-unemployed': {
    slug: 'long-term-unemployed',
    title: 'Long-Term Unemployed',
    h1: 'Practical small business ideas if you have gaps on your CV',
    description:
      'Low-barrier, dignity-first ideas with real proof of value. Start lean, show what you can do, and build momentum.',
    tone: 'respectful',
    howHelps: [
      'Surface practical, local services (e.g., handyman, delivery, tutoring)',
      'Build a brand and test outreach without needing a CV',
      'Record simple screen demos to show skills directly',
    ],
    defaultSkillIds: ['handyman', 'delivery', 'tutoring', 'local-services'],
    introVariants: [
      'Start {simple|practical}, prove value, {then raise prices|and build momentum}.',
      'Pick work you can show, not just tell—our templates help you demonstrate skill fast.',
    ],
    faqs: [
      { q: 'Do I need a CV?', a: 'No. We prioritise proof-of-work: demos, samples, and happy clients.' },
      { q: 'Budget flexibility?', a: 'From £1 to larger investments, you can scale at your pace.' },
    ],
    exampleIdeas: [
      { title: 'Local Assembly & Fix Service', whyFit: 'Immediate demand; trust via reviews.', steps: ['Pick 3 services', 'Post in 5 local groups', 'Collect 3 reviews'], tools: ['Facebook Groups', 'WhatsApp', 'Sheets'], costRange: '£1–£30' },
      { title: 'After-school Tutoring Starter', whyFit: 'Proof via short screen-demos and parents’ testimonials.', steps: ['Make 2 demo videos', 'Offer trial sessions', 'Gather testimonials'], tools: ['Canva', 'Zoom'], costRange: '£0–£50' },
    ],
    playbookByTime: {
      'weekend': ['Saturday: create demo', 'Sunday: post in groups and DM leads'],
      'part-time': ['2 evenings outreach', '1 evening delivery', 'Weekend reviews'],
      'full-time': ['Daily outreach', 'Daily delivery', 'Weekly referrals push'],
    },
  },
  'side-hustler': {
    slug: 'side-hustler',
    title: 'Side Hustlers with a Day Job',
    h1: 'Side hustle ideas you can run alongside your day job',
    description:
      'Automate the boring bits, grow smarter, and improve margins. Make your hustle work harder without burning out.',
    tone: 'energetic',
    howHelps: [
      'Explore adjacent niches (e.g., subscriptions, workshops)',
      'Unified inbox and cold email to reach influencers and stockists',
      'Pricing tools to test bundles and upsells',
    ],
    defaultSkillIds: ['ecommerce', 'email-marketing', 'content', 'automation'],
    introVariants: [
      '{Automate|Systemise} the boring bits so your evenings earn more than they cost.',
      'Choose models that compound: subscriptions, digital goods, workshops.',
    ],
    faqs: [
      { q: 'Time poor—worth it?', a: 'Yes. We optimise for leverage with automations and recurring offers.' },
      { q: 'Budget?', a: 'From £1 hacky starts to scaling with paid ads later.' },
    ],
    exampleIdeas: [
      { title: 'Newsletter Micro-SaaS + Sponsorships', whyFit: 'Asynchronous work that compounds.', steps: ['Define niche', 'Write 3 issues', 'Pitch 10 sponsors'], tools: ['Beehiiv', 'Zapier'], costRange: '£1–£50' },
      { title: 'Notion Templates Shop', whyFit: 'Low maintenance digital product.', steps: ['Create 3 templates', 'Open Gumroad', 'Launch on X/Reddit'], tools: ['Notion', 'Gumroad'], costRange: '£0–£50' },
    ],
    playbookByTime: {
      'weekend': ['Sat AM: build asset', 'Sat PM: launch', 'Sun: outreach 50 DMs'],
      'part-time': ['2 nights build', '1 night outreach', 'Weekend updates'],
      'full-time': ['Daily build/ship cadence', 'Weekly partnerships'],
    },
  },
  'young-entrepreneur': {
    slug: 'young-entrepreneur',
    title: 'Young Entrepreneurs (Low Income Areas)',
    h1: 'Starter business ideas for ambitious self-starters',
    description:
      "No gatekeeping. Validate quickly, build real skills, and prove yourself with visible wins—you're not waiting for luck.",
    tone: 'bold',
    howHelps: [
      'Turn flipping into a full resale brand with systems',
      'AI modules to build a site, automate outreach, track leads',
      'Record confident pitches for collaborators or investors',
    ],
    defaultSkillIds: ['resale', 'discord-community', 'growth', 'no-code'],
    introVariants: [
      'Skill up fast, show results publicly, {attract partners|earn trust}.',
      'Flip, document, and scale with systems—no permission needed.',
    ],
    faqs: [
      { q: 'Do I need capital?', a: 'Start with £1 flips or digital microservices; reinvest profits.' },
      { q: 'How to stand out?', a: 'Public build-in-steps and consistent mini-case studies.' },
    ],
    exampleIdeas: [
      { title: 'High-turnover Resale with SOPs', whyFit: 'Repeatable play with measurable KPIs.', steps: ['Pick 1 category', 'List 20 items', 'Track sell-through'], tools: ['eBay', 'Google Sheets'], costRange: '£10–£200' },
      { title: 'Discord Micro-Agency', whyFit: 'Community + services bundle.', steps: ['Spin up server', 'Offer 2 services', 'Close 3 retainers'], tools: ['Discord', 'Stripe'], costRange: '£0–£50' },
    ],
    playbookByTime: {
      'weekend': ['Sat: sourcing/build', 'Sun: sell/ship/outreach'],
      'part-time': ['3 nights: ops + posting', 'Weekend: sourcing'],
      'full-time': ['Daily KPIs', 'Weekly systems upgrades'],
    },
  },
  'midlife-career-shift': {
    slug: 'midlife-career-shift',
    title: 'Midlife Career Shifters',
    h1: 'Business ideas for thoughtful career shifters',
    description:
      'Your experience is your edge. Explore credible, low-risk service niches with clear structure and validation steps.',
    tone: 'warm',
    howHelps: [
      'Explore niches (grief coaching, menopause support, eldercare consulting)',
      'Branding and outreach to build authority and client trust',
      'Risk analysis to inform decisions before investing',
    ],
    defaultSkillIds: ['coaching', 'branding', 'wellness', 'community'],
    introVariants: [
      'Shape your next chapter with {measured risks|structured experiments}.',
      'Package experience into {credible|trusted} offers with clear outcomes.',
    ],
    faqs: [
      { q: 'Safe to invest now?', a: 'We stage investment from £1 trials to higher budgets, with risk gates.' },
      { q: 'How to build trust?', a: 'Authority content, social proof, and outcome-based offers.' },
    ],
    exampleIdeas: [
      { title: 'Menopause Support Coaching', whyFit: 'High trust, clear outcomes.', steps: ['Define 8-week program', 'Pilot 3 clients', 'Collect testimonials'], tools: ['Calendly', 'Zoom'], costRange: '£0–£100' },
      { title: 'Eldercare Navigation Service', whyFit: 'Practical, underserved need.', steps: ['Map providers', 'Create guide', 'Offer concierge calls'], tools: ['Notion', 'Airtable'], costRange: '£0–£100' },
    ],
    playbookByTime: {
      'weekend': ['Sat: outline offer', 'Sun: outreach 20 leads'],
      'part-time': ['2 nights authority content', '1 night calls'],
      'full-time': ['Daily pipeline', 'Weekly client delivery cadence'],
    },
  },
};
